import { useMemo, useState } from 'react';

import type {
  IFiltersBar,
  ISearchFilter,
  ISelectFilter,
  TFieldFilter,
  TInputFilter,
} from '../../../types/data_table';

import { ArrowsRepeat } from '../../../assets/Icons';

import { Button } from '../../Button';
import { SkeletonBase } from '../../SkeletonLoader/SkeletonBase';

import { AddFilterMenu } from './AddFilterMenu';
import { AppliedFilterChip } from './AppliedFilterChip';
import { InputFilterDropdown } from './InputFilterDropdown';
import { SearchFilterInput } from './SearchFilterInput';
import { SelectFilterDropdown } from './SelectFilterDropdown';
import { DEFAULT_COUNTRY, renderFieldFilterMenu } from './fieldFilterMenu';
import { useFieldFilters } from './useFieldFilters';

import styles from './filters-bar.module.scss';

/**
 * The row of controls above a `DataTable`, laid out in a fixed order: search
 * boxes, dropdowns, the chips for applied text/phone values, the control that
 * adds a new one, and finally the reset button.
 *
 * It is presentational — every change is reported up through
 * `filtersChangeHandler` and the container decides what it means. The only
 * state here is what has not been applied yet: pending checkbox ticks (inside
 * {@link SelectFilterDropdown}) and half-typed field values (inside
 * {@link useFieldFilters}).
 */
export const FiltersBar = ({
  lang,
  filtersList,
  filtersChangeHandler,
  resetFilters,
  isLoading,
  isMobile,
  country,
  localization,
  hasActiveFilters,
}: IFiltersBar) => {
  const { inputFilters, selectFilters, searchFilters } = useMemo(
    () => ({
      inputFilters: filtersList.filter(
        (filter) => filter?.type === 'text' || filter?.type === 'phone'
      ) as TInputFilter[],
      selectFilters: filtersList.filter(
        (filter) => filter?.type === 'select'
      ) as ISelectFilter[],
      searchFilters: filtersList.filter(
        (filter) => filter?.type === 'search'
      ) as ISearchFilter[],
    }),
    [filtersList]
  );

  /**
   * With exactly one filter and it being a text or phone one, there is nothing
   * to choose between: it is rendered inline instead of behind the "+" menu.
   */
  const loneInputFilter =
    filtersList.length === 1 && inputFilters.length === 1
      ? inputFilters[0]
      : undefined;

  /** Country the phone fields are currently formatted for. */
  const [selectedCountry, setSelectedCountry] = useState(
    country || DEFAULT_COUNTRY
  );

  /** Bumped to remount the "+" menu, closing any field left open inside it. */
  const [addFilterMenuKey, setAddFilterMenuKey] = useState(0);

  const {
    appliedFieldFilters,
    fieldErrors,
    searchValues,
    applyFieldFilter,
    removeFieldFilter,
    getAppliedValue,
    validateField,
    revalidateField,
    clearFieldErrors,
    handleSearchChange,
    resetFieldFilters,
  } = useFieldFilters({
    filtersList,
    inputFilters,
    onChange: filtersChangeHandler,
  });

  const isTableLoading = isLoading?.table;

  /** Applies a value typed into a text or phone field. */
  const handleFieldApply = (filter: TInputFilter, value: string) => {
    applyFieldFilter(filter, value);
    filtersChangeHandler(
      filter.id,
      filter.type,
      // The phone field hands back an international number; the API expects
      // it without the leading plus.
      filter.type === 'phone' ? value.replace(/^\+/, '') : value
    );
  };

  /** Removes an applied text, phone or search value. */
  const handleFieldClear = (filter: TFieldFilter) => {
    removeFieldFilter(filter);
    filtersChangeHandler(filter.id, filter.type, undefined);
  };

  const handleReset = () => {
    resetFilters?.();
    resetFieldFilters();
  };

  /** The pop-up shared by both ways of editing a text or phone filter. */
  const renderMenu = (filter: TInputFilter, onApply: (value: string) => void) =>
    renderFieldFilterMenu({
      filter,
      lang,
      isMobile,
      country: selectedCountry,
      localization,
      errorMsg: fieldErrors[filter.id],
      onValidate: validateField,
      // Picking a different country changes which pattern the number has to
      // match, so whatever is already typed is checked again against it.
      onCountryChange: (nextCountry, nextValidations) => {
        setSelectedCountry(nextCountry);
        revalidateField(filter.id, nextValidations);
      },
      onApply,
    });

  const renderFilters = () => (
    <>
      {searchFilters.map((filter, index) => (
        <SearchFilterInput
          key={`${filter.id}-filter-${index}`}
          filter={filter}
          index={index}
          value={searchValues[filter.id] ?? filter.value ?? ''}
          disabled={isTableLoading}
          onChange={(value) => handleSearchChange(filter, value)}
        />
      ))}

      {selectFilters.map((filter, index) => (
        <SelectFilterDropdown
          key={`${filter.id}-filter-${index}`}
          filter={filter}
          lang={lang}
          disabled={isTableLoading}
          localization={localization}
          onChange={filtersChangeHandler}
        />
      ))}

      {appliedFieldFilters
        // The lone input filter shows its value on its own button, so it must
        // not also get a chip.
        .filter((filter) => filter.id !== loneInputFilter?.id)
        .map((filter, index) => (
          <AppliedFilterChip
            key={`${filter.id}-filter-${index}`}
            filter={filter}
            disabled={isTableLoading}
            onClear={() => handleFieldClear(filter)}
          />
        ))}

      {loneInputFilter ? (
        <InputFilterDropdown
          filter={loneInputFilter}
          appliedValue={getAppliedValue(loneInputFilter.id)}
          disabled={isTableLoading}
          renderMenu={renderMenu}
          onApply={(value) => handleFieldApply(loneInputFilter, value)}
          onClear={() => handleFieldClear(loneInputFilter)}
        />
      ) : (
        inputFilters.length > 0 && (
          <AddFilterMenu
            key={`fields-filter-dropdown-${addFilterMenuKey}`}
            filters={inputFilters}
            disabled={isTableLoading}
            renderMenu={renderMenu}
            onApply={(filter, value) => {
              setAddFilterMenuKey((previous) => previous + 1);
              handleFieldApply(filter, value);
            }}
            // Dismissing the menu abandons whatever was being typed in it.
            onClose={() => {
              clearFieldErrors();
              setAddFilterMenuKey((previous) => previous + 1);
            }}
          />
        )
      )}

      {resetFilters && (
        <Button
          size="md"
          color="primary"
          variant="link"
          leadingIcon={ArrowsRepeat}
          text={localization?.resetFilters || 'Reset filters'}
          disabled={isTableLoading || !hasActiveFilters}
          onClickHandler={handleReset}
        />
      )}
    </>
  );

  return (
    <div
      data-filters-bar-is-mobile={isMobile}
      className={styles['filters-bar']}
    >
      {isLoading?.filters ? (
        <SkeletonBase height="2.5rem" />
      ) : (
        !!filtersList.length && renderFilters()
      )}
    </div>
  );
};
